import { useQuery, useQueries, useMutation } from '@tanstack/react-query';
import { queryClient } from '../queryClient';
import { QueryServerKeys } from 'constants/QueryServerKeys';
import { displayErrorMessage, displaySuccessMessage } from 'utils';
import { getErrorMessage } from 'utils/errors';
import {
  createNewClusterSize,
  deleteClusterSize,
  getCloudProviders,
  getClusterSizeOperators,
  getClusterSizesPerClusterCode,
  getClusterSizesPerProvider,
  getNodeLabels,
  getAffectedInstancesFromModifiedClusterSize,
  updateClusterSize,
  getClusterSizeRoles,
  getClusterSizesForUserRoleAndPlatformAndOperator,
} from 'services/capacityManagement';
import { InstancePlatform } from 'types/cluster';
import {
  NewClusterSizeBody,
  UpdatedClusterSize,
} from 'types/capacityManagement.types';

export function useGetCloudProviders() {
  return useQuery({
    queryKey: [QueryServerKeys.CAPACITY_MANAGEMENT.CLOUD_PROVIDERS],
    queryFn: async () => {
      try {
        const result = await getCloudProviders();
        return result?.data?.providers;
      } catch (error) {
        throw error;
      }
    },
    refetchOnWindowFocus: false,
      refetchOnReconnect: false,
      refetchOnMount: false,

  });
}

export function useClusterSizesPerCloudProviders({
  cloudProviderID,
}: {
  cloudProviderID: string;
}) {
  return useQuery({
    queryKey: [
      QueryServerKeys.CAPACITY_MANAGEMENT.CLUSTER_SIZES_PER_PROVIDER,
      cloudProviderID,
    ],
    queryFn: async () => {
      try {
        const result = await getClusterSizesPerProvider({ cloudProviderID });
        return result?.data?.data;
      } catch (error) {
        throw error;
      }
    },
    refetchOnWindowFocus: false,
      refetchOnReconnect: false,
      refetchOnMount: false,

  });
}

export function useClusterSizesPerClusterCode({
  clusterCode,
}: {
  clusterCode: string;
}) {
  return useQuery({
    queryKey: [
      QueryServerKeys.CAPACITY_MANAGEMENT
        .CLUSTER_SIZE_OPERATORS_PER_CLUSTER_CODE,
      clusterCode,
    ],
    queryFn: async () => {
      try {
        const result = await getClusterSizesPerClusterCode({
          clusterCode,
        });
        const data = result?.data.sizes?.slice(0, 6);
        return data;
      } catch (error) {
        throw error;
      }
    },
    enabled: !!clusterCode,
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
      refetchOnMount: false,

  });
}

export function useGetOperatorsPerPlatform({
  platform,
  operatorNameFilter,
}: {
  platform: InstancePlatform;
  operatorNameFilter?: string;
}) {
  return useQuery({
    queryKey: [
      QueryServerKeys.CAPACITY_MANAGEMENT.CLUSTER_SIZE_OPERATORS_PER_PROVIDER,
      platform,
      // ...(operatorNameFilter ? [operatorNameFilter] : []),
    ],
    queryFn: async () => {
      try {
        const result = await getClusterSizeOperators({ platform });
        return result;
      } catch (error) {
        throw error;
      }
    },
    refetchOnWindowFocus: false,
      refetchOnReconnect: false,
      refetchOnMount: false,
      select(res) {
        if (operatorNameFilter) {
          const filterOperator = res.data.platformOperators.filter(
            operator => operator.clusterCode === operatorNameFilter,
          );
          res = {
            ...res,
            data: {
              ...res.data,
              platformOperators: filterOperator,
            },
          };
        }
        return res;
      },


  });
}

export function useGetClusterSizeRoles({
  isForCustomSize,
}: {
  isForCustomSize: boolean;
}) {
  return useQuery({
    queryKey: [QueryServerKeys.CAPACITY_MANAGEMENT.CLUSTER_SIZE_ROLES, isForCustomSize],
    queryFn: async () => {
      try {
        const result = await getClusterSizeRoles({ isForCustomSize });
        return result?.data?.roles;
      } catch (error) {
        throw error;
      }
    },
    refetchOnWindowFocus: false,
      refetchOnReconnect: false,
      refetchOnMount: false,

  });
}


export function useGetNodeLabels({
  region,
  platform,
  k8sClusterCode,
}: {
  region: string;
  platform: InstancePlatform;
  k8sClusterCode: string;
}) {
  return useQuery({
    queryKey: [QueryServerKeys.CAPACITY_MANAGEMENT.NODE_LABELS],
    queryFn: async () => {
      try {
        const result = await getNodeLabels({
          region,
          platform,
          k8sClusterCode,
        });
        return result?.data;
      } catch (error) {
        throw error;
      }
    },
    refetchOnWindowFocus: false,
      refetchOnReconnect: false,
      refetchOnMount: false,

  });
}

export function useGetAffectedInstancesFromModifiedClusterSize({
  sizeID,
}: {
  sizeID: number;
}) {
  return useQuery({
    queryKey: [QueryServerKeys.CAPACITY_MANAGEMENT.AFFECTED_CLUSTERS, sizeID],
    queryFn: async () => {
      try {
        const result = await getAffectedInstancesFromModifiedClusterSize({
          sizeID,
        });
        return result?.data;
      } catch (error) {
        throw error;
      }
    },
    refetchOnWindowFocus: false,
      refetchOnReconnect: false,
      refetchOnMount: false,

  });
}

export function useCreateNewClusterSize() {
  const queryOptions = useMutation({
    mutationKey: [QueryServerKeys.CAPACITY_MANAGEMENT.CREATE_NEW_CLUSTER_SIZE],
    mutationFn: async ({
      newClusterSize,
    }: {
      newClusterSize: NewClusterSizeBody;
    }) => {
      const res = await createNewClusterSize({ newClusterSize });
      return res;
    },
    onSuccess(result) {
      queryClient.invalidateQueries({ queryKey: [QueryServerKeys.CAPACITY_MANAGEMENT.CLUSTER_SIZES_PER_PROVIDER] });
      displaySuccessMessage(result.data.message);
    },
    onError(error: any) {
      let errorMessage = error && getErrorMessage(error);
      if (errorMessage) {
        displayErrorMessage(errorMessage);
      }
    },
  });
  return queryOptions;
}

export function useUpdateClusterSize() {
  const queryOptions = useMutation({
    mutationKey: [QueryServerKeys.CAPACITY_MANAGEMENT.UPDATE_CLUSTER_SIZE],
    mutationFn: async ({
      updatedClusterSize,
      sizeID,
    }: {
      updatedClusterSize: UpdatedClusterSize;
      sizeID: number;
    }) => {
      const res = await updateClusterSize({ updatedClusterSize, sizeID });
      return res;
    },
    onSuccess(result) {
      queryClient.invalidateQueries({ queryKey: [QueryServerKeys.CAPACITY_MANAGEMENT.CLUSTER_SIZES_PER_PROVIDER] });
      displaySuccessMessage(result.data.message);
    },
    onError(error: any) {
      let errorMessage = error && getErrorMessage(error);
      if (errorMessage) {
        displayErrorMessage(errorMessage);
      }
    },
  });
  return queryOptions;
}

export type BulkClusterSizeUpdate = {
  sizeID: number;
  clusterCode: string;
  updatedClusterSize: UpdatedClusterSize;
};

export type BulkClusterSizeUpdateResult = {
  sizeID: number;
  clusterCode: string;
  error?: string;
};

// One PUT per size cell (there is no bulk endpoint). Every request runs even when some fail, the
// sizes are refetched once, and the caller gets a per-cell outcome instead of a toast per call.
export function useBulkUpdateClusterSizes() {
  return useMutation({
    mutationKey: [
      QueryServerKeys.CAPACITY_MANAGEMENT.UPDATE_CLUSTER_SIZE,
      'bulk',
    ],
    mutationFn: async (
      updates: BulkClusterSizeUpdate[],
    ): Promise<BulkClusterSizeUpdateResult[]> => {
      const settled = await Promise.allSettled(
        updates.map(({ sizeID, updatedClusterSize }) =>
          updateClusterSize({ sizeID, updatedClusterSize }),
        ),
      );
      return settled.map((outcome, index) => ({
        sizeID: updates[index].sizeID,
        clusterCode: updates[index].clusterCode,
        error:
          outcome.status === 'rejected'
            ? getErrorMessage(outcome.reason) || 'Update failed'
            : undefined,
      }));
    },
    onSettled() {
      queryClient.invalidateQueries({
        queryKey: [QueryServerKeys.CAPACITY_MANAGEMENT.CLUSTER_SIZES_PER_PROVIDER],
      });
    },
  });
}

export function useAffectedInstancesForSizes(sizeIDs: number[]) {
  return useQueries({
    queries: sizeIDs.map(sizeID => ({
      queryKey: [QueryServerKeys.CAPACITY_MANAGEMENT.AFFECTED_CLUSTERS, sizeID],
      queryFn: async () => {
        const result = await getAffectedInstancesFromModifiedClusterSize({
          sizeID,
        });
        return result?.data;
      },
      refetchOnWindowFocus: false,
    })),
  });
}

export function useDeleteClusterSize() {
  const queryOptions = useMutation({
    mutationKey: [QueryServerKeys.CAPACITY_MANAGEMENT.DELETE_CLUSTER_SIZE],
    mutationFn: async ({ sizeID }: { sizeID: number }) => {
      const res = await deleteClusterSize({ sizeID });
      return res;
    },
    onSuccess(result) {
      queryClient.invalidateQueries({ queryKey: [QueryServerKeys.CAPACITY_MANAGEMENT.CLUSTER_SIZES_PER_PROVIDER] });
      displaySuccessMessage(result.data.message);
    },
    onError(error: any) {
      let errorMessage = error && getErrorMessage(error);
      if (errorMessage) {
        displayErrorMessage(errorMessage);
      }
    },
  });
  return queryOptions;
}

export function useClusterSizesForUserRoleAndPlatformAndOperator({
  instanceID,
  pod,
}: {
  instanceID: string;
  pod: 'analytics' | 'loader';
}) {
  return useQuery({
    queryKey: [
      QueryServerKeys.CAPACITY_MANAGEMENT
        .CLUSTER_SIZES_FOR_USER_ROLE_PLATFORM_OPERATOTR,
      instanceID,
    ],
    queryFn: async () => {
      try {
        const result = await getClusterSizesForUserRoleAndPlatformAndOperator({
          instanceID,
        });
        return result.data;
      } catch (error) {
        throw error;
      }
    },
    refetchOnWindowFocus: false,
      refetchOnReconnect: false,
      refetchOnMount: false,
      select(data) {
        const shouldAddAnalyticsSize =
          pod === 'analytics' &&
          data.sizes.findIndex(d => d.id === data?.analyticsSize.id) < 0
            ? true
            : false;
        const shouldAddLoaderSize =
          pod === 'loader' &&
          data.sizes.findIndex(d => d.id === data?.loaderSize.id) < 0
            ? true
            : false;
        const newArr = {
          sizes: [
            ...data?.sizes,
            ...(shouldAddAnalyticsSize ? [data?.analyticsSize] : []),
            ...(shouldAddLoaderSize ? [data?.loaderSize] : []),
          ],
          currentSize: pod === 'analytics' ? data?.analyticsSize : data?.loaderSize,
          allowedSizeIDs: data.allowedSizeIDs,
          ownerRole: data.ownerRole,
        };
        return newArr;
      },

  });
}
